import {
  BACCARAT_DRAGON_BONUS_ID,
  BACCARAT_ID,
  BACCARAT_KO_ID,
  BACCARAT_NO_COMMISSION_ID,
  BACCARAT_QUEENCO_ID,
  BACCARAT_SUPER_SIX_ID,
  EZ_BACCARAT_ID,
  GAME_ID_FROM_URL,
} from '@ezugi/constants';
import { queryParams } from '@ezugi/utils';

export const REDESIGNED_BACCARAT_GAME_IDS = [
  BACCARAT_ID,
  BACCARAT_KO_ID,
  BACCARAT_NO_COMMISSION_ID,
  BACCARAT_DRAGON_BONUS_ID,
  BACCARAT_QUEENCO_ID,
  BACCARAT_SUPER_SIX_ID,
  EZ_BACCARAT_ID,
];

export const getFontSizeBasedOnLanguage = (isMobilePortraitDevice, language) => {
  const supportedLanguages = [ 'ta', 'ms' ];
  if (isMobilePortraitDevice && supportedLanguages.includes(language.code)) {
    return '9.5px';
  }
  return '12px';
};

const mobileRules = {
  firstRule: [ 'de', 'hu' ],
  secondRule: [ 'pt', 'ta' ],
  thirdRule: [ 'tr' ],
  forthRule: [ 'es', 'fr', 'lt' ],
  notSuitedTieRules: [ 'pt', 'es', 'fr', 'lt', 'bg', 'it', 'ja', 'kh' ],
  notSuitedTieLongWords: [ 'de', 'hu' ],
};

const desktopRules = {
  firstRule: [ 'bg', 'lt', 'lv', 'nl', 'hu', 'es' ],
  secondRule: [ 'tr', 'de' ],
  thirdRule: [ 'kh' ],
};

const translationRules = {
  notSuitedTie: {
    rules: mobileRules.notSuitedTieRules,
    translation: 'translate(1, 0)',
  },
  notSuitedTieLongWords: {
    rules: mobileRules.notSuitedTieLongWords,
    translation: 'translate(9, 0)',
  },
  suitedTie: [
    { code: 'bg', translation: 'translate(1, 2)' },
    { rules: mobileRules.firstRule, translation: 'translate(10, 3)' },
    { rules: mobileRules.secondRule, translation: 'translate(3, 0)' },
    { codes: mobileRules.forthRule, translation: 'translate(3, 2)' },
    { code: 'nl', translation: 'translate(3, 1)' },
    { code: 'it', translation: 'translate(0, 2)' },
    { code: 'lv', translation: 'translate(2, 2)' },
    { code: 'ro', translation: 'translate(2, 1)' },
    { rules: mobileRules.thirdRule, translation: 'translate(3, 3)', alternativeTranslation: 'translate(0, 3)' },
  ],
};

const getTranslationRulesForLanguage = (language, suitedTieEnabled) => {
  const { code } = language;

  if (!suitedTieEnabled) {
    if (translationRules.notSuitedTie.rules.includes(code)) {
      return translationRules.notSuitedTie.translation;
    }
    if (translationRules.notSuitedTieLongWords.rules.includes(code)) {
      return translationRules.notSuitedTieLongWords.translation;
    }
  } else {
    for (const rule of translationRules.suitedTie) {
      if ((rule.code && rule.code === code) || (rule.rules && rule.rules.includes(code))) {
        return suitedTieEnabled ? rule.translation : (rule.alternativeTranslation || rule.translation);
      }
      if (rule.codes && rule.codes.includes(code)) {
        return rule.translation;
      }
    }
  }

  return 'translate(0, 0)';
};

const getMobileTranslation = (suitedTieEnabled, language) => getTranslationRulesForLanguage(language, suitedTieEnabled);

const getDesktopTranslation = (language, suitedTieEnabled) => {
  if (desktopRules.firstRule.includes(language.code)) {
    return 'translate(292, 35)';
  }
  if (desktopRules.secondRule.includes(language.code)) {
    return 'translate(292, 38)';
  }
  if (desktopRules.thirdRule.includes(language.code)) {
    return 'translate(292, 30)';
  }
  if (language.code === 'de') {
    return 'translate(290, 38)';
  }
  if (language.code === 'it') {
    return 'translate(292, 34)';
  }
  if (suitedTieEnabled && language.code === 'ro') {
    return 'translate(292, 36)';
  }

  return 'translate(292, 32)';
};

export const getRuleBasedOnLanguage = (suitedTieEnabled, isMobilePortraitDevice, language) => (isMobilePortraitDevice
  ? getMobileTranslation(suitedTieEnabled, language)
  : getDesktopTranslation(language, suitedTieEnabled));

export const setLabelY = (suitedTieEnabled, language) => {
  if (suitedTieEnabled) {
    return [ 32, 41, 20, 30 ];
  }
  if (language.code === 'de' || language.code === 'tr') {
    return [ 37, 41, 32, 30 ];
  }

  return [ 35, 47, 32, 34 ];
};

export const setClassNameBasedOnLanguage = (suitedTieEnabled, language) => {
  const supportedLanguages = [
    'de',
    'es',
    'fr',
    'it',
    'pt',
    'ro',
    'ru',
    'ta',
    'th',
    'tl',
    'hu',
    'tr',
    'lt',
    'lv',
    'nl',
  ];

  if (!suitedTieEnabled) {
    if (supportedLanguages.includes(language.code)) {
      return true;
    }
  }
  return false;
};

export const getWidthBasedOnLanguage = (language) => {
  const languages = [ 'de', 'hu' ];
  if (languages.includes(language.code)) {
    return 80;
  }
  return 95;
};

export const getSideBetFontSizeBasedOnLanguage = (language, suitedTieEnabled) => {
  const languages = [ 'tr', 'de', 'lt', 'lv', 'nl', 'es', 'fr', 'pt' ];
  if (languages.includes(language.code)) {
    return suitedTieEnabled ? '8px' : '11px';
  }
  if (language.code === 'ro') {
    return '10px';
  }
  return '11px';
};

export const getTiePayoutTransformBasedOnLanguage = (language, isMobilePortraitDevice, suitedTieEnabled) => {
  const { code } = language;

  const tiePayoutTranslations = {
    tr: suitedTieEnabled
      ? (isMobilePortraitDevice ? 'translate(2, -1)' : 'translate(3, -2)')
      : (isMobilePortraitDevice ? 'translate(0, -1)' : 'translate(0, 5)'),

    de: suitedTieEnabled
      ? (isMobilePortraitDevice ? 'translate(3, -1)' : 'translate(3, 1)')
      : (isMobilePortraitDevice ? 'translate(0, 1)' : 'translate(0, 5)'),

    es: suitedTieEnabled
      ? 'translate(4, -2)'
      : (isMobilePortraitDevice ? 'translate(0, 1)' : 'translate(-1, 2)'),

    nl: suitedTieEnabled
      ? 'translate(3, -2)'
      : (isMobilePortraitDevice ? 'translate(0, -1)' : 'translate(-1, 0)'),

    pt: suitedTieEnabled
      ? 'translate(3, -2)'
      : (isMobilePortraitDevice ? 'translate(0, 1)' : 'translate(-1, 2)'),

    fr: suitedTieEnabled
      ? 'translate(3, -2)'
      : (isMobilePortraitDevice ? 'translate(0, -1)' : 'translate(0, 3)'),

    lt: isMobilePortraitDevice
      ? 'translate(3, -2)'
      : (suitedTieEnabled ? 'translate(3, -1)' : 'translate(1, -1)'),

    lv: suitedTieEnabled
      ? 'translate(3, -1)'
      : 'translate(1, -1)',

    ru: suitedTieEnabled && !isMobilePortraitDevice
      ? 'translate(0, 0)'
      : 'translate(0, -2)',

    ro: suitedTieEnabled && isMobilePortraitDevice
      ? 'translate(2, -1)'
      : 'translate(0, 0)',
  };

  return tiePayoutTranslations[code] || 'translate(0, 0)';
};

export const getSuitedTiePayoutTransformBasedOnLanguage = (language, isMobilePortraitDevice) => {
  const { code } = language;
  const suitedTieTranslations = {
    lv: isMobilePortraitDevice ? 'translate(4, 3)' : 'translate(1, 1)',
    lt: isMobilePortraitDevice ? 'translate(4, 3)' : 'translate(1, 1)',
    es: isMobilePortraitDevice ? 'translate(5, 2)' : 'translate(1, 1)',
    fr: isMobilePortraitDevice ? 'translate(4, 2)' : 'translate(1, 1)',
    tr: isMobilePortraitDevice ? 'translate(3, 1)' : 'translate(2, -1)',
    nl: isMobilePortraitDevice ? 'translate(4, 1)' : 'translate(1, 1)',
    pt: isMobilePortraitDevice ? 'translate(4, -2)' : 'translate(1, 1)',
    ro: isMobilePortraitDevice ? 'translate(0, -2)' : 'translate(-1, -1)',
    de: isMobilePortraitDevice ? 'translate(4, 1)' : 'translate(1, 1)',
    it: isMobilePortraitDevice ? 'translate(0, 0)' : 'translate(-2, -1)',
    hu: isMobilePortraitDevice ? 'translate(0, 0)' : 'translate(0, -1)',
    ru: isMobilePortraitDevice ? 'translate(0, 0)' : 'translate(0, -2)',
    bg: isMobilePortraitDevice ? 'translate(0, 0)' : 'translate(0, -1)',
  };

  return suitedTieTranslations[code] || 'translate(0, 0)';
};

export const preventZoom = (e) => {
  if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=')) {
    e.preventDefault();
  }
};

export const isCWonURL = GAME_ID_FROM_URL === 45;
export const isDTonURL = GAME_ID_FROM_URL === 24;

export const TABLE_ID_FROM_URL = queryParams.params.tableId;
